import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ProductComplianceTechnicalHeadingTypeEnum } from '@inter-ikea-kompis/enums';
import type { IProduct, IProductComplianceTechnical, ITheme } from '@inter-ikea-kompis/types';
import { StringUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Table, Text } from '@ingka/common-styles-webc';

import type IProductInformationSectionLocalizedInformation from '../../types/IProductInformationSectionLocalizedInformation.js';
import type IProductInformationSectionTranslations from '../../types/IProductInformationSectionTranslations.js';
import TechnicalInformationCardStyle from './TechnicalInformationCardStyle.js';

const TECHNICAL_INFORMATION_SORT_ORDER = [
	'BRAND',
	'MODEL',
	'PRODUCT_TYPE',
	'PRODUCT_DESCRIPTION',
	'ENERGY_STAR',
	'ENERGY_EFFICIENCY_CLASS',
	'ENERGY_CONSUMPTION',
	'CONSUMPTION'
];

const TECHNICAL_INFORMATION_EXCLUDED_HEADINGS = [
	'PRODUCT_INFO_SHEET',
	'PRODUCT_FICHE',
	'HYPERLINK'
];

/**
 * This is the technical information card sub component to Product information section.
 */

export default class TechnicalInformationCard extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: IProductInformationSectionLocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IProductInformationSectionTranslations | null = null;

	// A product with the type IProduct.
	@property({ type: Object, required: true })
	public product!: IProduct;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		const headingTextClass = Text.classnames({ heading: { m: true } });

		return html`
			<style>
				${TechnicalInformationCardStyle.getStyle({ theme: this.theme })}
			</style>

			<div class="container">
				<h2 id="text" class="header ${headingTextClass}">
					${this.getTranslations().productCardTechnicalInformationTitle}
				</h2>

				${this.getProductDescription()}

				<table class="${Table.classnames({ fullWidth: true })}">
					<tbody>
						${this.getDataRows()}
					</tbody>
				</table>
			</div>
		`;
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation?.translations;
	}

	private getTranslations(): IProductInformationSectionTranslations {
		return this.translations
			? this.translations
			: <IProductInformationSectionTranslations>this.localizedInformation?.translations;
	}

	/**
	 * Retrieves product description if exists.
	 */
	private getProductDescription(): RenderResult {
		if (!this.product.content?.typeName || !this.product?.content?.name) {
			return null;
		}

		const headingTextClass = Text.classnames({ heading: { xs: true } });
		const bodyTextClass = Text.classnames({ body: { m: true } });

		return html`
			<section class="productDescription">
				<h3 id="text" class="${headingTextClass}">${this.product.content.name}</h3>

				<span class="${bodyTextClass}">
					${StringUtility.capitalizeFirstLetter(this.product.content?.typeName)}
				</span>
			</section>
		`;
	}

	/**
	 * Retrieves data from complianceTechnical or technicalInformation.
	 */
	private getDataRows(): RenderResult {
		const complianceTechnicalItems = this.getComplianceTechnical();
		const bodyTextClass = Text.classnames({ body: { m: true } });

		if (complianceTechnicalItems) {
			return html`
				${complianceTechnicalItems.map(
					({ headingText, text }) => html`
						<tr class="${bodyTextClass}">
							<td>${headingText}</td>
							<td>${text}</td>
						</tr>
					`
				)}
			`;
		} else {
			return html`${this.product.content?.technicalInformation?.map(
				({ typeName, valueMetric, unitMetric = '' }) => html`
					<tr class="${bodyTextClass}">
						<td>${typeName}</td>
						<td>${valueMetric} ${unitMetric}</td>
					</tr>
				`
			)}`;
		}
	}

	/**
	 * Retrieves all objects in complianceTechnical.
	 */
	private getComplianceTechnical(): IProductComplianceTechnical[] | null {
		const compliances = this.product.content?.complianceTechnical?.filter(
			(complianceItem) =>
				complianceItem.headingType !== ProductComplianceTechnicalHeadingTypeEnum.nonRescaled
		);

		if (compliances) {
			return this.getSortedComplianceTechnical(compliances);
		}

		return null;
	}

	/**
	 * Get sorted and filtered complianceTechnical. It will remove excluded headers from the EXCLUDED_HEADINGS array.
	 * Sorting: It will sort the complianceTechnical data by the SORT_ORDER array, which is the same sorting done
	 * on the PIP page on IKEA.com. There are some things that are sorted and some are left unsorder, which means
	 * that they will always be displayed last. This is done because IKEA.com wants to highlight some of the data
	 * and some should just be displayed, regardless of order.
	 *
	 * @param items ...
	 */
	private getSortedComplianceTechnical(
		items: IProductComplianceTechnical[]
	): IProductComplianceTechnical[] {
		const sortOrder = TECHNICAL_INFORMATION_SORT_ORDER;
		const excludedHeaders = TECHNICAL_INFORMATION_EXCLUDED_HEADINGS;
		const compliancesToShowInCard = items.filter(
			(item) => !excludedHeaders.includes(item.headingType)
		);
		const sortedComplianceTechnical: IProductComplianceTechnical[] = compliancesToShowInCard
			.filter((compliance) => sortOrder.includes(compliance.headingType))
			.sort((a, b) => sortOrder.indexOf(a.headingType) - sortOrder.indexOf(b.headingType));

		const unsortedComplianceTechnical: IProductComplianceTechnical[] =
			compliancesToShowInCard.filter((compliance) => !sortOrder.includes(compliance.headingType));

		return [...sortedComplianceTechnical, ...unsortedComplianceTechnical];
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-technical-information-card': TechnicalInformationCard;
	}
}
